/**
 * <RyedoreLogo /> — single source of truth for the Ryedore brand mark.
 *
 * Two variants:
 *   - "mark"     — square icon, suitable for sidebar (collapsed) and favicons.
 *                  Falls back to the legacy dark wordmark if the new mark
 *                  asset is missing on disk.
 *   - "wordmark" — horizontal "RYEDORE" with the leaf graphic, suitable
 *                  for login / splash / marketing surfaces. ~3.5:1 ratio.
 *
 * Sizes are pixel heights; width auto-scales to preserve the asset's
 * native aspect ratio. The wordmark uses a wider intrinsic ratio so don't
 * pass a `width`-equal box around it; let the image breathe.
 *
 * Why a component (not just an <img>): we ship the logo to many places
 * (sidebar collapsed/expanded, login screen, splash screens, future
 * marketing-grade pages). Centralising the path + alt + a11y attrs here
 * means a rebrand is one Edit, not a grep-and-replace tour.
 *
 * Usage:
 *   <RyedoreLogo size={32} />                      // small mark (sidebar collapsed)
 *   <RyedoreLogo variant="wordmark" size={48} />   // login / splash / marketing
 */
import type { CSSProperties } from 'react'

type LogoVariant = 'mark' | 'wordmark'

interface RyedoreLogoProps {
  /** Render height in px. Width follows naturally per the asset's aspect. */
  size?: number
  /** "mark" = square icon, "wordmark" = horizontal Ryedore lockup. */
  variant?: LogoVariant
  /** Optional className for layout overrides. */
  className?: string
  /** Override the alt text. Defaults to "Ryedore". */
  alt?: string
}

const ASSET_FOR_VARIANT: Record<LogoVariant, string> = {
  // The mark file ships at /assets/ryedore-mark.png (sourced from logo-1.png).
  // We deliberately fall through to the legacy dark logo when the new mark
  // asset is missing on disk — keeps every existing deployment rendering.
  mark: '/assets/ryedore-mark.png',
  wordmark: '/assets/ryedore-wordmark.png',
}

export default function RyedoreLogo({
  size = 32,
  variant = 'mark',
  className,
  alt = 'Ryedore',
}: RyedoreLogoProps) {
  const src = ASSET_FOR_VARIANT[variant]
  // The wordmark's native aspect is ~3.5:1; the mark is 1:1. Letting the
  // browser size the width via `auto` avoids a squashed lockup when callers
  // forget to widen the parent container.
  const style: CSSProperties = { height: size, width: 'auto' }

  return (
    <img
      src={src}
      alt={alt}
      style={style}
      className={className}
      // Loading priority: brand-critical for first paint, never lazy.
      loading="eager"
      decoding="sync"
      onError={(e) => {
        // Graceful fallback to the legacy dark logo if the new asset is
        // missing — keeps SSO / first-boot screens looking coherent.
        const img = e.currentTarget as HTMLImageElement
        if (!img.dataset.fallbackTried) {
          img.dataset.fallbackTried = '1'
          img.src = '/assets/ryedore-logo-dark.png'
        }
      }}
    />
  )
}
